import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';


const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toIsoDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseIsoDate = (value) => {
  const iso = toIsoDate(value);
  if (!iso) return null;
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDisplayDate = (value) => {
  const iso = toIsoDate(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  return `${day}-${month}-${year}`;
};

const isDateDisabled = (date, minDate, maxDate) => {
  const iso = toIsoDate(date);
  const minIso = toIsoDate(minDate);
  const maxIso = toIsoDate(maxDate);
  return Boolean((minIso && iso < minIso) || (maxIso && iso > maxIso));
};

function SingleDatePicker({
  id,
  name,
  value,
  onChange,
  className = 'ui-input',
  placeholder = 'Select date',
  minDate,
  maxDate,
  disabled = false,
  style,
}) {
  const wrapperRef = useRef(null);
  const selectedDate = parseIsoDate(value);
  const todayIso = toIsoDate(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());

  useEffect(() => {
    if (selectedDate) {
      setViewDate(selectedDate);
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const emitChange = (nextValue) => {
    onChange?.({
      target: {
        id,
        name: name || id,
        value: nextValue,
      },
    });
  };

  const calendarDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(year, month, 1 - firstDay.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      return date;
    });
  }, [viewDate]);

  const moveMonth = (step) => {
    setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + step, 1));
  };

  const moveYear = (step) => {
    setViewDate((current) => new Date(current.getFullYear() + step, current.getMonth(), 1));
  };

  const handleSelectDate = (date) => {
    if (isDateDisabled(date, minDate, maxDate)) return;
    emitChange(toIsoDate(date));
    setIsOpen(false);
  };

  const handleClear = () => {
    emitChange('');
    setIsOpen(false);
  };

  const handleToday = () => {
    const today = new Date();
    if (isDateDisabled(today, minDate, maxDate)) return;
    setViewDate(today);
    emitChange(todayIso);
    setIsOpen(false);
  };

  return (
    <div className="tw-relative tw-w-full single-date-picker" ref={wrapperRef}>
      <div className={"tw-relative [&_input]:tw-cursor-pointer [&_input]:tw-pr-[44px] single-date-picker__field"}>
        <input
          type="text"
          id={id}
          name={name || id}
          value={formatDisplayDate(value)}
          readOnly
          disabled={disabled}
          className={className}
          style={style}
          placeholder={placeholder}
          autoComplete="off"
          onClick={() => !disabled && setIsOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsOpen((open) => !open);
            }
            if (event.key === 'Escape') {
              setIsOpen(false);
            }
          }}
        />
        <button
          type="button"
          className={"tw-absolute tw-top-[50%] tw-right-[12px] [transform:translateY(-50%)] tw-w-[28px] tw-h-[28px] tw-border-0 tw-rounded-[50%] tw-text-[#111827] tw-bg-[transparent] tw-inline-flex tw-items-center tw-justify-center tw-text-[21px] tw-leading-[1] [&:hover:not(:disabled)]:tw-bg-[#eef4ff] [&:hover:not(:disabled)]:tw-text-[#2554f4] [&:disabled]:tw-text-[#9ca3af] [&:disabled]:tw-cursor-not-allowed single-date-picker__trigger"}
          onClick={() => !disabled && setIsOpen((open) => !open)}
          disabled={disabled}
          aria-label="Open calendar"
        >
          <Icon icon="mdi:calendar-month-outline" />
        </button>
      </div>

      {isOpen && (
        <div className={"tw-absolute tw-left-0 tw-bottom-[calc(100%_+_10px)] tw-z-[1065] tw-w-[min(260px,_calc(100vw_-_32px))] tw-p-[8px] tw-border-[1px] tw-border-solid tw-border-[#d7dce5] tw-rounded-[8px] tw-bg-[#ffffff] tw-shadow-[0_18px_45px_rgba(15,_23,_42,_0.18)] [&::after]:[content:''] [&::after]:tw-absolute [&::after]:tw-right-[22px] [&::after]:tw-bottom-[-8px] [&::after]:tw-w-[14px] [&::after]:tw-h-[14px] [&::after]:tw-border-r-[1px] [&::after]:tw-border-r-[#d7dce5] [&::after]:tw-border-b-[1px] [&::after]:tw-border-solid [&::after]:tw-border-b-[#d7dce5] [&::after]:tw-bg-[#ffffff] [&::after]:[transform:rotate(45deg)] max-[480px]:tw-w-[min(260px,_calc(100vw_-_24px))] single-date-picker__popover"} role="dialog" aria-label="Choose date">
          <div className="tw-grid tw-grid-cols-[28px_28px_1fr_28px_28px] tw-gap-[4px] tw-items-center tw-mb-[8px] single-date-picker__header">
            <button type="button" className={"tw-min-h-[30px] tw-border-[1px] tw-border-solid tw-border-[#edf0f5] tw-rounded-[6px] tw-bg-[#f8fafc] tw-text-[#111827] tw-inline-flex tw-items-center tw-justify-center tw-text-[19px] [&:hover]:tw-border-[#94b7ff] [&:hover]:tw-text-[#2554f4] [&:hover]:tw-bg-[#f2f6ff] single-date-picker__nav"} onClick={() => moveYear(-1)} aria-label="Previous year">
              <Icon icon="mdi:chevron-double-left" />
            </button>
            <button type="button" className={"tw-min-h-[30px] tw-border-[1px] tw-border-solid tw-border-[#edf0f5] tw-rounded-[6px] tw-bg-[#f8fafc] tw-text-[#111827] tw-inline-flex tw-items-center tw-justify-center tw-text-[19px] [&:hover]:tw-border-[#94b7ff] [&:hover]:tw-text-[#2554f4] [&:hover]:tw-bg-[#f2f6ff] single-date-picker__nav"} onClick={() => moveMonth(-1)} aria-label="Previous month">
              <Icon icon="mdi:chevron-left" />
            </button>
            <div className="tw-min-h-[30px] tw-border-[1px] tw-border-solid tw-border-[#edf0f5] tw-rounded-[6px] tw-bg-[#f8fafc] tw-text-[#111827] tw-grid tw-grid-cols-[1fr_1fr] tw-items-center [justify-items:center] tw-font-bold tw-text-[13px] tw-tracking-[0] single-date-picker__month">
              <span>{MONTH_NAMES[viewDate.getMonth()]}</span>
              <span>{viewDate.getFullYear()}</span>
            </div>
            <button type="button" className={"tw-min-h-[30px] tw-border-[1px] tw-border-solid tw-border-[#edf0f5] tw-rounded-[6px] tw-bg-[#f8fafc] tw-text-[#111827] tw-inline-flex tw-items-center tw-justify-center tw-text-[19px] [&:hover]:tw-border-[#94b7ff] [&:hover]:tw-text-[#2554f4] [&:hover]:tw-bg-[#f2f6ff] single-date-picker__nav"} onClick={() => moveMonth(1)} aria-label="Next month">
              <Icon icon="mdi:chevron-right" />
            </button>
            <button type="button" className={"tw-min-h-[30px] tw-border-[1px] tw-border-solid tw-border-[#edf0f5] tw-rounded-[6px] tw-bg-[#f8fafc] tw-text-[#111827] tw-inline-flex tw-items-center tw-justify-center tw-text-[19px] [&:hover]:tw-border-[#94b7ff] [&:hover]:tw-text-[#2554f4] [&:hover]:tw-bg-[#f2f6ff] single-date-picker__nav"} onClick={() => moveYear(1)} aria-label="Next year">
              <Icon icon="mdi:chevron-double-right" />
            </button>
          </div>

          <div className="tw-grid tw-grid-cols-[repeat(7,_minmax(0,_1fr))] tw-p-[0_0_6px] tw-border-b-[1px] tw-border-solid tw-border-b-[#e5e7eb] tw-text-[#4b5563] tw-text-[12px] tw-font-bold tw-text-center single-date-picker__weekdays">
            {WEEK_DAYS.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="tw-grid tw-grid-cols-[repeat(7,_minmax(0,_1fr))] tw-gap-[1px] tw-pt-[6px] single-date-picker__grid">
            {calendarDays.map((date) => {
              const iso = toIsoDate(date);
              const isSelected = iso === toIsoDate(value);
              const isToday = iso === todayIso;
              const isOutsideMonth = date.getMonth() !== viewDate.getMonth();
              const isDisabled = isDateDisabled(date, minDate, maxDate);

              return (
                <button
                  type="button"
                  key={iso}
                  className={[
                    "[aspect-ratio:1] tw-min-w-[0] tw-border-[1px] tw-border-solid tw-border-[transparent] tw-rounded-[50%] tw-bg-[transparent] tw-text-[#111827] tw-text-[12px] tw-font-semibold [&:hover:not(:disabled)]:tw-border-[#94b7ff] [&:hover:not(:disabled)]:tw-bg-[#eef4ff] [&:hover:not(:disabled)]:tw-text-[#2554f4] [&.is-outside]:tw-text-[#9ca3af] [&.is-today]:tw-border-[#2554f4] [&.is-today]:tw-text-[#2554f4] [&.is-selected]:tw-border-[#2554f4] [&.is-selected]:tw-bg-[#2554f4] [&.is-selected]:tw-text-[#ffffff] [&.is-selected]:tw-shadow-[0_5px_12px_rgba(37,_84,_244,_0.26)] [&.is-disabled]:tw-text-[#c7ccd5] [&.is-disabled]:tw-cursor-not-allowed single-date-picker__day",
                    isSelected ? "is-selected" : "",
                    isToday ? "is-today" : "",
                    isOutsideMonth ? "is-outside" : "",
                    isDisabled ? "is-disabled" : "",
                  ].filter(Boolean).join("      ")}
                  onClick={() => handleSelectDate(date)}
                  disabled={isDisabled}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className={"tw-flex tw-justify-between tw-pt-[8px] [&_button]:tw-border-0 [&_button]:tw-bg-[transparent] [&_button]:tw-text-[#2554f4] [&_button]:tw-font-bold [&_button]:tw-p-[3px_6px] [&_button]:tw-text-[13px] [&_button:hover:not(:disabled)]:tw-underline [&_button:disabled]:tw-text-[#9ca3af] [&_button:disabled]:tw-cursor-not-allowed single-date-picker__footer"}>
            <button type="button" onClick={handleClear}>Clear</button>
            <button type="button" onClick={handleToday} disabled={isDateDisabled(new Date(), minDate, maxDate)}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SingleDatePicker;
